/**
 * What diverged between what an operator typed and what is now saved, and how a choice is applied.
 *
 * **Written as pure functions over plain records** so the dialog is a rendering of a decision rather
 * than the place the decision is made. That matters because the rule this enforces is a safety
 * property — nothing is written until every divergence has been answered — and a property enforced
 * only inside a component is a property nobody can test without a browser.
 *
 * **A conflict is per FIELD, not per record.** A 409 from the version guard says "somebody saved
 * first", and the naive answer is to offer "reload" or "overwrite". Overwrite is the wrong default
 * in this product: the other person's change is invisible from here, and the records are people's
 * identity documents. So every field that actually differs is listed, with both values shown, and
 * the operator answers each one. A field both sides changed to the SAME value is not a conflict and
 * is not shown, because asking about it would teach people to click through the list.
 */

/** One value as either side holds it. `null` is an absent value, which is a real state. */
type FieldValue = string | null;

/** A record as the dialog compares it: a flat map of field path to displayed value. */
export type FieldMap = Readonly<Record<string, FieldValue>>;

/** Which side a divergence was answered with. */
export type Resolution = 'mine' | 'theirs';

export interface FieldConflict {
  /** The dotted path the API knows the field by, which is also the dialog's row key. */
  readonly path: string;
  /** What the operator has in front of them. */
  readonly mine: FieldValue;
  /** What is saved on the server now. */
  readonly theirs: FieldValue;
}

/** A stable, locale-independent ordering, so the dialog's rows never depend on where it was built. */
function byPath(left: string, right: string): number {
  if (left < right) {
    return -1;
  }
  return left > right ? 1 : 0;
}

/**
 * The fields that actually differ, in a stable order.
 *
 * The union of both key sets rather than one of them: a field the other side ADDED is a divergence
 * this operator cannot see, and leaving it out would let their save silently remove it.
 */
export function conflictsBetween(mine: FieldMap, theirs: FieldMap): readonly FieldConflict[] {
  const paths = [...new Set([...Object.keys(mine), ...Object.keys(theirs)])].sort(byPath);

  return paths
    .map((path) => ({ path, mine: mine[path] ?? null, theirs: theirs[path] ?? null }))
    .filter((candidate) => candidate.mine !== candidate.theirs);
}

/** Whether every listed divergence has been answered. Nothing is submitted until this is true. */
export function fullyResolved(
  conflicts: readonly FieldConflict[],
  choices: Readonly<Record<string, Resolution | undefined>>,
): boolean {
  return conflicts.every((conflict) => choices[conflict.path] !== undefined);
}

/**
 * The record to submit, given the saved version and a choice per divergent field.
 *
 * It starts from THEIRS and applies the operator's `mine` choices over it, rather than starting
 * from `mine`: any field the other side changed and this operator did not is then kept by
 * construction, including one that is not in this form at all. Starting from `mine` would make
 * every field the operator never touched an implicit overwrite.
 */
export function applyResolutions(
  theirs: FieldMap,
  mine: FieldMap,
  choices: Readonly<Record<string, Resolution | undefined>>,
): FieldMap {
  const merged: Record<string, FieldValue> = { ...theirs };
  for (const [path, choice] of Object.entries(choices)) {
    if (choice === 'mine') {
      merged[path] = mine[path] ?? null;
    }
  }
  return merged;
}
